import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { PAY_RULE_GROUPS, assignedRules, isRuleOn } from "./hours";

const PANEL_WIDTH = 300;

/**
 * Every pay rule one technician can hold, in a single dropdown.
 *
 * Rendered from the PAY_RULE_GROUPS catalog rather than written out per rule,
 * so the next rule is one entry in `hours.js` and appears here with its saving,
 * summary and keyboard behaviour already working.
 *
 * It is a multi-select, not a plain dropdown, because the rules are not
 * alternatives: someone is on daily-8 *and* can earn both double-time premiums.
 * A single-select control could only ever hold one of the four, so the catalog
 * carries a `select` mode per group and this renders radios or checkboxes to
 * match — the exclusivity lives in the policy, not in the widget.
 *
 * Saves on click rather than behind a Save button, matching the role dropdown in
 * Team → Manage, so there is no half-applied state to reason about. Each change
 * is sent as its own patch: sending the whole classification every time would
 * make a stale row overwrite a rule someone else had just changed.
 */
const PayRulesDropdown = ({
  user,
  saving,
  disabled,
  onToggle,
  // Optional: offered at the foot of the panel. For a person this is "follow
  // the branch again" — it clears their own flags so the branch's apply.
  onReset,
  resetLabel,
  canReset = false,
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const panelRef = useRef(null);
  const [rect, setRect] = useState(null);

  const assigned = useMemo(() => assignedRules(user), [user]);

  /**
   * What the closed control reads.
   *
   * Two rules fit side by side; beyond that it collapses to the first plus a
   * count. Letting the list keep growing would truncate mid-label — and the
   * catalog is expected to grow, so a summary that only works for today's four
   * rules would break quietly as they are added. The full list is on the title
   * attribute and one click away either way.
   */
  const summary = useMemo(() => {
    if (!assigned.length) return "Unassigned";
    if (assigned.length <= 2) return assigned.map((option) => option.chip).join(" · ");
    return `${assigned[0].chip} +${assigned.length - 1}`;
  }, [assigned]);

  // Portaled and fixed. The drawer body scrolls and clips its overflow, which
  // would cut this panel off on the lower rows — the same reason
  // BranchExclusionEditor and the unassigned-rule popover portal theirs.
  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const anchor = anchorRef.current?.getBoundingClientRect();
      if (!anchor) return;
      // Flip above the trigger when there is not room below, so a member near
      // the bottom of a long list does not open a panel off-screen.
      const ESTIMATED_HEIGHT = 260;
      const below = window.innerHeight - anchor.bottom;
      const flipUp = below < ESTIMATED_HEIGHT && anchor.top > below;
      setRect({
        top: flipUp ? null : Math.round(anchor.bottom + 6),
        bottom: flipUp ? Math.round(window.innerHeight - anchor.top + 6) : null,
        left: Math.round(
          Math.max(8, Math.min(anchor.right - PANEL_WIDTH, window.innerWidth - PANEL_WIDTH - 8))
        ),
      });
    };
    place();

    const onPointerDown = (event) => {
      if (anchorRef.current?.contains(event.target)) return;
      if (panelRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  // The panel stays open across a click. Assigning several rules to one person
  // is the normal case, and closing after each would mean reopening it three
  // times to finish classifying someone.
  const choose = (group, option) => {
    if (group.select === "one") {
      // Clicking the selected rule again clears it: payroll needs a way to undo
      // a misclassification, and unassigned is a real state rather than an
      // absence of one.
      onToggle({ [option.field]: isRuleOn(user, option) ? null : option.value });
      return;
    }
    onToggle({ [option.field]: !isRuleOn(user, option) });
  };

  const clearGroup = (group) => onToggle({ [group.field]: null });

  return (
    <span ref={anchorRef} className="inline-flex">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((previous) => !previous)}
        aria-expanded={open}
        aria-haspopup="true"
        title={
          disabled
            ? "You do not have permission to change this"
            : assigned.length
            ? assigned.map((option) => option.label).join(" · ")
            : "No policies assigned"
        }
        className={`w-[190px] h-8 px-2.5 rounded-[6px] ring-1 ring-inset flex items-center justify-between gap-2 text-[13px] leading-[18px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
          assigned.length
            ? "bg-white text-[#1A2332] font-OpenSansSemiBold ring-[#E5E8F3] hover:bg-[#F7F9FC]"
            : "bg-[#FFFBEB] text-[#B45309] font-OpenSansSemiBold ring-[#FDE68A] hover:bg-[#FEF3C7]"
        }`}
      >
        <span className="truncate">{summary}</span>
        <svg width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true" className="shrink-0">
          <path
            d="M1 1l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && rect
        ? createPortal(
            <div
              ref={panelRef}
              style={{
                left: rect.left,
                top: rect.top ?? undefined,
                bottom: rect.bottom ?? undefined,
                width: PANEL_WIDTH,
              }}
              className="fixed z-[70] rounded-[10px] border border-[#E5E8F3] bg-white py-1.5 shadow-[0px_12px_32px_rgba(16,24,40,0.16)]"
            >
              {PAY_RULE_GROUPS.map((group, groupIndex) => (
                <div
                  key={group.key}
                  className={groupIndex > 0 ? "mt-1 pt-1 border-t border-[#F1F5F9]" : ""}
                >
                  <div className="px-3 py-1 flex items-baseline justify-between gap-2">
                    <span className="text-[11px] leading-4 font-OpenSansSemiBold text-[#1A2332] uppercase tracking-[0.5px]">
                      {group.label}
                    </span>
                    <span className="text-[11px] leading-4 font-OpenSansRegular text-[#868DA6]">
                      {group.hint}
                    </span>
                  </div>

                  {group.options.map((option) => {
                    const on = isRuleOn(user, option);
                    return (
                      <button
                        key={option.id}
                        type="button"
                        role={group.select === "one" ? "radio" : "checkbox"}
                        aria-checked={on}
                        disabled={saving}
                        onClick={() => choose(group, option)}
                        className={`w-full px-3 py-1.5 flex items-center gap-2.5 text-left text-[13px] leading-[18px] disabled:opacity-50 disabled:cursor-not-allowed ${
                          on
                            ? "bg-[#EAF3FF] text-[#0058E5] font-OpenSansSemiBold"
                            : "text-[#45556C] font-OpenSansRegular hover:bg-[#F7F9FC]"
                        }`}
                      >
                        {/* A round marker for the choose-one group and a square
                            one for the any-that-apply group: the shape is what
                            tells someone whether picking this will replace their
                            current answer or add to it. */}
                        <span
                          aria-hidden="true"
                          className={`shrink-0 w-[15px] h-[15px] border flex items-center justify-center ${
                            group.select === "one" ? "rounded-full" : "rounded-[4px]"
                          } ${on ? "bg-[#0058E5] border-[#0058E5]" : "bg-white border-[#CBD5E1]"}`}
                        >
                          {on ? (
                            group.select === "one" ? (
                              <span className="w-[5px] h-[5px] rounded-full bg-white" />
                            ) : (
                              <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                                <path
                                  d="M1 3.5L3.4 6 8 1"
                                  stroke="#fff"
                                  strokeWidth="1.6"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            )
                          ) : null}
                        </span>
                        <span className="min-w-0 truncate">{option.label}</span>
                      </button>
                    );
                  })}

                  {/* Only the choose-one group needs this. A checkbox group is
                      cleared by unticking it, but a radio set has no way back to
                      "none" from inside the list — and unassigned is a state
                      payroll has to be able to return someone to. */}
                  {group.select === "one" && group.field ? (
                    <button
                      type="button"
                      disabled={saving || (user?.[group.field] ?? null) === null}
                      onClick={() => clearGroup(group)}
                      className="w-full px-3 py-1.5 pl-[42px] text-left text-[12px] leading-[18px] font-OpenSansRegular text-[#868DA6] hover:bg-[#F7F9FC] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    >
                      Clear — leave unassigned
                    </button>
                  ) : null}
                </div>
              ))}
              {onReset ? (
                <div className="mt-1 pt-1 border-t border-[#F1F5F9]">
                  <button
                    type="button"
                    disabled={saving || !canReset}
                    onClick={onReset}
                    className="w-full px-3 py-1.5 text-left text-[12px] leading-[18px] font-OpenSansRegular text-[#0058E5] hover:bg-[#F7F9FC] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                  >
                    {resetLabel}
                  </button>
                </div>
              ) : null}
            </div>,
            document.body
          )
        : null}
    </span>
  );
};

export default PayRulesDropdown;
